import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import actions from "../../actions";
import { createSubCategory, getUserById, updateSubCategory } from "../../apis";
import { toast } from "react-toastify";
import { FiMoreVertical } from "react-icons/fi"; // Import the triple dots icon
import "./SubcategoryPage.css";
import Pagination from "@mui/material/Pagination"; // Using MUI Pagination

const SubcategoryPage = ({ startLoadingRequest, stopLoadingRequest }) => {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [subCategoryName, setSubCategoryName] = useState("");
  const [stylePreference, setStylePreference] = useState("");
  const [editingSubCategoryId, setEditingSubCategoryId] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(null); // State to manage open dropdown
  const dropdownRefs = useRef([]); // Reference to manage dropdowns
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Items per page
  const [filteredSubcategories, setFilteredSubcategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [errors, setErrors] = useState({});


  const { id } = useParams();
  const fetchCategoriesData = async () => {
    try {
      // console.log("Fetching categories data for user ID:", id);
      startLoadingRequest();
      const response = await getUserById(id);
      // console.log("Response from getUserById:", response.data)
      const { categories, subCategory } = response.data.data;
      setCategories(categories || []);
      setSubcategories(subCategory || []);
      setFilteredSubcategories(subCategory || []);
      stopLoadingRequest();
      setLoading(false);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setSubcategories([]);
      setCategories([]);
      setSelectedCategory("");
      stopLoadingRequest();
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchCategoriesData();
    }
  }, [id]);

  // Filter subcategories based on selected category
  useEffect(() => {
    if (!selectedCategory) {
      setFilteredSubcategories(subcategories); // If no category is selected, show all
    } else {
      const categoryExists = categories.filter(
        (category) => category._id === selectedCategory
      )[0];
      const filtered = subcategories.filter(
        (subcategory) => ((subcategory.categoryId?._id === selectedCategory)|| (categoryExists?.name.toLowerCase() === subcategory.categoryId?.name.toLowerCase()))
      );
      setFilteredSubcategories(filtered);
    }
  }, [selectedCategory, subcategories]);

  // Reset current page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Further filter based on search term (by subcategory name)
  const searchedSubcategories = filteredSubcategories.filter((subcategory) =>
    (subcategory.name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination helper: slice items based on currentPage and itemsPerPage
  const paginateItems = (items) => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return items.slice(indexOfFirstItem, indexOfLastItem);
  };

  const paginatedSubcategories = paginateItems(searchedSubcategories);
  const totalPages = Math.ceil(searchedSubcategories.length / itemsPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleAddSubCategory = () => {
    setSubCategoryName("");
    setStylePreference("");
    setCategoryId(null);
    setEditingSubCategoryId(null);
    setIsModalOpen(true);
  };

  const handleEdit = (subcategory) => {
    setEditingSubCategoryId(subcategory._id);
    setSubCategoryName(subcategory.name);
    setStylePreference(subcategory.stylePreference || "");
    setCategoryId(subcategory.categoryId?._id || null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleCreateSubCategory = async (data, catid) => {
    try {
      startLoadingRequest();
      const subCategoryFormData = {
        userId: id,
        name: subCategoryName,
        stylePreference,
      };
      const response = await createSubCategory(subCategoryFormData, categoryId);
      if (response && response.data) {
        await fetchCategoriesData();
        toast.success("Subcategory created successfully!");
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      toast.error("Failed to create subcategory.");
    } finally {
      stopLoadingRequest();
      closeModal();
    }
  };

  const handleUpdateSubCategory = async (data) => {
    try {
      startLoadingRequest();
      const subCategoryFormData = {
        userId: id,
        name: subCategoryName,
        subcategoryId: editingSubCategoryId,
        stylePreference,
      };
      const response = await updateSubCategory(subCategoryFormData);
      if (response && response.data) {
        await fetchCategoriesData();
        toast.success("Subcategory updated successfully!");
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      toast.error("Failed to update Subcategory.");
    } finally {
      stopLoadingRequest();
      closeModal();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = {};

  if (!subCategoryName.trim()) {
    validationErrors.subCategoryName = "Subcategory name is required.";
  }

  if (!categoryId) {
    validationErrors.categoryId = "Category selection is required.";
  }

  if (Object.keys(validationErrors).length > 0) {
    setErrors(validationErrors);
    return;
  }

  setErrors({});
    const subCategoryFormData = {
      userId: id,
      name: subCategoryName,
      stylePreference,
    };
    if (editingSubCategoryId) {
      subCategoryFormData.subcategoryId = editingSubCategoryId;
      await handleUpdateSubCategory(subCategoryFormData);
    } else {
      await handleCreateSubCategory(subCategoryFormData);
    }
  };

  const toggleDropdown = (index) => {
    setDropdownOpen(dropdownOpen === index ? null : index);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="subcategory-page">
      {/* Header with Title and Search */}
      <div
        className="header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <h1>Subcategories</h1>
        <div className="search-container">
          <input
            type="text"
            placeholder="Search Subcategories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      {/* Category Select & Add Button */}
      <div className="category-select">
        <div>
          <label htmlFor="category">Select Category:</label>
          <select
            id="category"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{ border: "1px solid #000", backgroundColor: "#E7E5E3" }}
          >
            <option value="">ALL</option>
            {categories?.map((category) => (
              <option key={category._id} value={category._id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
        {/* <button
          className="add-subcategory-button"
          id="add-subcategory-button-color"
          onClick={handleAddSubCategory}
        >
          Add Subcategory
        </button> */}
      </div>
      {/* Subcategory Table */}
      <div className="subcategory-table-container">
        <table className="subcategory-table">
          <thead>
            <tr>
              <th>S.No</th>
               <th>Category</th>
              <th>Subcategory Name</th>
              <th>Item Count</th>
              {/* <th>Actions</th> */}
            </tr>
          </thead>
          <tbody>
            {paginatedSubcategories?.map((subcategory, index) => (
              <tr key={subcategory._id} style={{ position: "relative" }}>
                <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                 <td>{subcategory.categoryId?.name||"Others"}</td>
                <td>{subcategory.name}</td>
                <td>{subcategory.wardrobeCount}</td>
                {/* <td>
                  <div
                    className="table-actions"
                    ref={(el) => (dropdownRefs.current[index] = el)}
                  >
                    <div className="dropdown">
                      <FiMoreVertical
                        size={24}
                        onClick={() => toggleDropdown(index)}
                        className="dropdown-icon"
                      />
                      {dropdownOpen === index && (
                        <button
                          onClick={() => {
                            toggleDropdown(null);
                            handleEdit(subcategory);
                          }}
                          className="dropdown-item"
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 30,
                            width: "85px",
                            height: "30px",
                          }}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                </td> */}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      <div className="pagination" style={{ paddingTop: "20px" }}> 
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
        />
      </div>
      {/* Modal for Add/Edit */}
      {isModalOpen && (
        <div
          style={{
            zIndex: "10000",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            backdropFilter: "blur(10px)",
          }}
        >
          <div
            className="modal-content"
            style={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              padding: "20px",
              width: "100%",
              maxWidth: "600px",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h2 style={{ margin: 0 }}>Add New Subcategory</h2>
              <span
                className="close"
                onClick={closeModal}
                style={{ cursor: "pointer", fontSize: "24px", fontWeight: "bold" }}
              >
                &times;
              </span>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group" style={{ marginBottom: "20px" }}>
                <label htmlFor="subCategoryName" style={{ display: "block", marginBottom: "8px" }}>
                  Subcategory Name
                </label>
                <input
                  type="text"
                  id="subCategoryName"
                  name="subCategoryName"
                  value={subCategoryName}
                  onChange={(e) => setSubCategoryName(e.target.value)}
                  required
                  style={{
                    width: "100%",
                    padding: "10px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                  />
                  {errors.subCategoryName && (
                    <small style={{ color: "red",fontSize:"12px" }}>{errors.subCategoryName}</small>
                  )}
              </div>
              <div className="form-group" style={{ marginBottom: "20px" }}>
                <label htmlFor="category" style={{ display: "block", marginBottom: "8px" }}>
                  Select Category:
                </label>
                <select
                  id="category"
                  value={categoryId || ""}
                  onChange={(e) => setCategoryId(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "15px",
                    
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                >
                  <option value="">Select Category</option>
                  {categories?.map((category) => (
                    <option key={category._id} value={category._id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {errors.categoryId && (
    <small style={{ color: "red",fontSize:"12px" }}>{errors.categoryId}</small>
  )}
              </div>
              <div
                className="modal-actions"
                style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}
              >
                <button
                  type="button"
                  className="cancel-button"
                  onClick={closeModal}
                  style={{
                    padding: "10px 20px",
                    borderRadius: "4px",
                    border: "none",
                    cursor: "pointer",
                    backgroundColor: "#090909",
                    color: "#dfdfdf",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-button"
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#090909",
                    color: "#dfdfdf",
                    borderRadius: "4px",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      logoutRequest: actions.auth.logout,
      stopLoadingRequest: actions.loader.stopLoading,
      startLoadingRequest: actions.loader.startLoading,
    },
    dispatch
  );

export default connect(null, mapDispatchToProps)(SubcategoryPage);
